import { useState, useEffect, Fragment, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

import { api } from '../services/client';
import { useTranslation } from '../i18n';
import { IAuditLog } from '../types';
import { formatDateTime } from '../utils/formatDate';
import { Card } from './ui/card';
import { ChevronDown, ChevronRight, Search } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from './ui/table';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { AUDIT_ACTION_TYPES, AUDIT_MODULES } from '../constants/auditActions';
import { getActionStyle } from '../utils/auditStyles';
import AuditExpandedDetail from './AuditExpandedDetail';
import Pagination from './Pagination';

// One list with the API's EAuditModule (mirrored in constants/auditActions),
// so a module the API logs under can always be filtered on (SITOSP-383).
const MODULE_FILTERS = AUDIT_MODULES.map((value) => ({ value, label: value }));

const MAX_DATE_RANGE_DAYS = 90;
const PAGE_LIMIT = 20;

const AuditLogs = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  // Initialize state from URL query params
  const [logs, setLogs] = useState<IAuditLog[]>([]);
  const [page, setPage] = useState(() => Number(searchParams.get('page')) || 1);
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [search, setSearch] = useState(() => searchParams.get('search') || '');
  const [entityFilter, setEntityFilter] = useState(
    () => searchParams.get('module') || '',
  );
  const [actionType, setActionType] = useState(
    () => searchParams.get('actionType') || '',
  );
  const [startDate, setStartDate] = useState(() => {
    const param = searchParams.get('startDate');

    if (param) {
      return param;
    }

    const d = new Date();
    d.setDate(d.getDate() - 7);
    return d.toISOString().slice(0, 10);
  });
  const [endDate, setEndDate] = useState(() => {
    const param = searchParams.get('endDate');

    if (param) {
      return param;
    }

    return new Date().toISOString().slice(0, 10);
  });

  /** Sync current filter state to URL query string */
  const updateQueryString = useCallback(
    (p: number, s: string, mod: string, at: string, sd: string, ed: string) => {
      const params = new URLSearchParams();

      if (p > 1) params.set('page', String(p));
      if (s) params.set('search', s);
      if (mod) params.set('module', mod);
      if (at) params.set('actionType', at);
      if (sd) params.set('startDate', sd);
      if (ed) params.set('endDate', ed);

      setSearchParams(params, { replace: true });
    },
    [setSearchParams],
  );

  const getActionDesc = (action: string) =>
    t(`audit.action.${action}` as any) || action;

  const getModuleLabel = (mod: string) => {
    const key = mod || 'unknown';
    return t(`audit.module.${key}` as any);
  };

  const loadLogs = async (
    p: number,
    s = search,
    ent = entityFilter,
    at = actionType,
    sd = startDate,
    ed = endDate,
  ) => {
    setLoading(true);
    updateQueryString(p, s, ent, at, sd, ed);

    try {
      const params = new URLSearchParams({
        page: String(p),
        limit: String(PAGE_LIMIT),
      });

      if (s) params.set('search', s);
      if (ent) params.set('module', ent);
      if (at) params.set('actionType', at);
      if (sd) params.set('startDate', sd);
      if (ed) params.set('endDate', ed);

      const data = await api.get<{
        data: IAuditLog[];
        total: number;
        page: number;
        limit: number;
      }>(`/backoffice/audit-logs?${params.toString()}`);
      setLogs(data.data);
      setTotal(data.total);
      setPage(data.page);
      setTotalPages(Math.ceil(data.total / data.limit));
    } catch (err) {
      console.error('Failed to load audit logs:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLogs(page, search, entityFilter, actionType, startDate, endDate);
  }, []);

  // Date range validation (max days)
  const getDateRangeError = (): string | null => {
    if (!startDate || !endDate) return null;
    const start = new Date(startDate);
    const end = new Date(endDate);
    if (isNaN(start.getTime()) || isNaN(end.getTime())) return null;
    const diffDays = Math.ceil(
      (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24),
    );
    if (diffDays > MAX_DATE_RANGE_DAYS)
      return t('audit.date_range_exceeded', {
        days: String(MAX_DATE_RANGE_DAYS),
      });
    if (diffDays < 0) return t('audit.date_range_invalid');
    return null;
  };

  // Debounced filter removed — now manual Apply triggers search

  const handleApplyFilters = () => {
    const error = getDateRangeError();
    if (error) {
      toast.error(error);
      return;
    }
    loadLogs(1, search, entityFilter, actionType, startDate, endDate);
  };

  const handleClearFilters = () => {
    setSearch('');
    setEntityFilter('');
    setActionType('');
    const d = new Date();
    d.setDate(d.getDate() - 7);
    const defaultStart = d.toISOString().slice(0, 10);
    const defaultEnd = new Date().toISOString().slice(0, 10);
    setStartDate(defaultStart);
    setEndDate(defaultEnd);
    loadLogs(1, '', '', '', defaultStart, defaultEnd);
  };

  const handlePageChange = (p: number) => {
    loadLogs(p);
  };

  const handleToggleExpand = (id: string) => {
    setExpandedId((prev) => (prev === id ? null : id));
  };

  if (loading && logs.length === 0) {
    return (
      <div className="p-4 lg:p-8 text-muted-foreground">
        {t('common.loading')}
      </div>
    );
  }

  return (
    <div className="p-4 lg:p-8 flex flex-col gap-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">{t('audit.title')}</h1>
        <p className="text-base text-gray-600 mt-2">{t('audit.subtitle')}</p>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl p-4 border border-border">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto_auto_auto_auto_auto] gap-3 items-center">
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
              <Search className="w-5 h-5" />
            </span>
            <Input
              type="text"
              placeholder={t('audit.search_placeholder')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select
            value={actionType || 'all'}
            onValueChange={(val) => setActionType(val === 'all' ? '' : val)}
          >
            <SelectTrigger className="w-full lg:w-[260px]">
              <SelectValue placeholder={t('audit.all_actions')} />
            </SelectTrigger>
            <SelectContent className="max-h-[320px]">
              <SelectItem value="all">{t('audit.all_actions')}</SelectItem>
              {AUDIT_ACTION_TYPES.map((action) => (
                <SelectItem key={action} value={action}>
                  {getActionDesc(action)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={entityFilter || 'all'}
            onValueChange={(val) => setEntityFilter(val === 'all' ? '' : val)}
          >
            <SelectTrigger className="w-full lg:w-[180px]">
              <SelectValue placeholder={t('audit.all_modules')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('audit.all_modules')}</SelectItem>
              {MODULE_FILTERS.map((mod) => (
                <SelectItem key={mod.value} value={mod.value}>
                  {t(`audit.module.${mod.label}` as any)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="flex items-center gap-2">
            <Input
              type="date"
              value={startDate}
              max={endDate || new Date().toISOString().slice(0, 10)}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-[160px]"
              placeholder={t('audit.start_date')}
            />
            <span className="text-muted-foreground text-sm">—</span>
            <Input
              type="date"
              value={endDate}
              max={new Date().toISOString().slice(0, 10)}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-[160px]"
              placeholder={t('audit.end_date')}
            />
          </div>
          <Button variant="default" size="sm" onClick={handleApplyFilters}>
            {t('common.apply')}
          </Button>
          <Button variant="outline" size="sm" onClick={handleClearFilters}>
            {t('common.clear_all_filters')}
          </Button>
        </div>
      </div>

      {/* Table */}
      <Card className="gap-0 overflow-hidden">
        {logs.length === 0 ? (
          <div className="p-12 text-center text-sm text-muted-foreground">
            {t('audit.no_logs')}
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <Table className="table-fixed" style={{ minWidth: '800px' }}>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[20%]">
                      {t('audit.admin_id')}
                    </TableHead>
                    <TableHead className="w-[20%]">
                      {t('audit.module')}
                    </TableHead>
                    <TableHead className="w-[30%]">
                      {t('audit.details')}
                    </TableHead>
                    <TableHead className="w-[30%]">
                      {t('audit.timestamp')}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {logs.map((log) => {
                    const {
                      id,
                      username,
                      action,
                      module: logModule,
                      beforeValues,
                      afterValues,
                      metadata,
                      createdAt,
                    } = log;

                    const hasDetails =
                      beforeValues !== null ||
                      afterValues !== null ||
                      metadata !== null;
                    const isExpanded = expandedId === id;
                    const adminLabel = username || '—';
                    const desc = getActionDesc(action);

                    return (
                      <Fragment key={id}>
                        <TableRow
                          className="hover:bg-muted/50 transition-colors cursor-pointer"
                          onClick={() => hasDetails && handleToggleExpand(id)}
                        >
                          <TableCell className="text-sm">
                            <div className="flex items-center gap-2">
                              {hasDetails ? (
                                isExpanded ? (
                                  <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0" />
                                ) : (
                                  <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
                                )
                              ) : (
                                <span className="w-4 h-4 shrink-0" />
                              )}
                              <span className="truncate" title={adminLabel}>
                                {adminLabel}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell className="text-sm font-bold">
                            {getModuleLabel(logModule || '')}
                          </TableCell>
                          <TableCell>
                            <span
                              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getActionStyle(action)}`}
                            >
                              {desc}
                            </span>
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                            {formatDateTime(createdAt)}
                          </TableCell>
                        </TableRow>

                        {isExpanded && hasDetails && (
                          <tr>
                            <td
                              colSpan={4}
                              className="px-6 py-4 bg-muted/30 border-b border-border"
                            >
                              <AuditExpandedDetail
                                beforeValues={beforeValues}
                                afterValues={afterValues}
                                metadata={metadata}
                              />
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </div>

            <Pagination
              page={page}
              totalPages={totalPages}
              total={total}
              limit={PAGE_LIMIT}
              onPageChange={handlePageChange}
            />
          </>
        )}
      </Card>
    </div>
  );
};

export default AuditLogs;
